import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import type { DetrixSession } from './types';
import { SessionViewerApp } from './app/SessionViewerApp';
import { usePlaybackStore } from './store/usePlaybackStore';
import { useSessionUiStore } from './store/useSessionUiStore';
import type { TabId } from './store/tabId';
import { useSessionPlayback } from './hooks/useSessionPlayback';
import { findFirstErrorInSession } from './utils/sessionErrorSignals';
import type { SessionViewerAPI } from './core/control/SessionViewerAPI';
import type { DetrixPlugin } from './core/control/DetrixPlugin';

export type { SessionViewerAPI } from './core/control/SessionViewerAPI';
export type { DetrixPlugin } from './core/control/DetrixPlugin';

export type SessionViewerProps = {
  session: DetrixSession;
  initialTimeMs?: number;
  initialTab?: TabId;
  selectedItemId?: string | null;
  initialFocus?: 'error' | 'none';
  plugins?: readonly DetrixPlugin[];
};

export function createSessionViewerApi(session: DetrixSession): SessionViewerAPI {
  return {
    setPlayhead(ms) {
      usePlaybackStore.getState().setPlayheadMs(ms, { pause: true });
    },
    selectItem(id) {
      useSessionUiStore.getState().setSelectedId(id);
    },
    focusError() {
      const hit = findFirstErrorInSession(session);
      if (!hit) return;
      const ui = useSessionUiStore.getState();
      const pb = usePlaybackStore.getState();
      ui.setActiveTab(hit.tab);
      ui.setSelectedId(hit.id);
      pb.setPlayheadMs(hit.time, { pause: true });
    },
  };
}

export const SessionViewer = forwardRef<SessionViewerAPI, SessionViewerProps>(function SessionViewer(
  { session, initialTimeMs, initialTab, selectedItemId, initialFocus, plugins },
  ref,
) {
  useSessionPlayback(session);

  useImperativeHandle(ref, () => createSessionViewerApi(session), [session]);

  const appliedSessionIdRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (appliedSessionIdRef.current === session.sessionId) return;
    appliedSessionIdRef.current = session.sessionId;

    const ui = useSessionUiStore.getState();
    const pb = usePlaybackStore.getState();

    if (initialFocus === 'error') {
      ui.setFocusErrorsOnly(true);
      const hit = findFirstErrorInSession(session);
      if (hit) {
        ui.setActiveTab(hit.tab);
        ui.setSelectedId(hit.id);
        pb.setPlayheadMs(hit.time, { pause: true });
        return;
      }
    }

    if (initialTab) ui.setActiveTab(initialTab);
    if (selectedItemId !== undefined) ui.setSelectedId(selectedItemId);
    if (initialTimeMs !== undefined) pb.setPlayheadMs(initialTimeMs, { pause: true });
  }, [session, session.sessionId, initialTimeMs, initialTab, selectedItemId, initialFocus]);

  useEffect(() => {
    if (!plugins?.length) return;
    const api = createSessionViewerApi(session);
    const cleanups: Array<void | (() => void)> = [];
    for (const p of plugins) {
      cleanups.push(p.init({ session, api }));
    }
    return () => {
      for (const c of cleanups) {
        if (typeof c === 'function') c();
      }
    };
  }, [session.sessionId, session, plugins]);

  return <SessionViewerApp session={session} />;
});

SessionViewer.displayName = 'SessionViewer';
